import { DEFAULT_EXPLORER_REGION_QUERY_LIMIT } from '@components/Explorer/MapBounds';
import { getGroupsIn } from '@db/Groups/Get';
import {
  getGroupsPlans,
  getGroupsPlansInMapBounds,
  getUsersPlans,
  getUsersPlansInMapBounds,
} from '@db/Plans';
import { getUsersIn } from '@db/Users';
import type { MapRegion } from '@helpers/Map';
import { uniqueBy } from '@helpers/Parsers';
import db from '@helpers/firebase/Firestore';
import type { AppQueryKeyValue } from '@helpers/query';
import {
  AppQueryDomain,
  AppQueryKeySegment,
  createQueryIdListKeyPart,
  createQueryKey,
  nullableQueryKeyPart,
  useAppQuery,
} from '@helpers/query';
import type { User } from '@types';

export type UseExplorerPlansQueryOptions = {
  /** Set false while the initial map region or route params are not ready. */
  enabled?: boolean;
  /** Group ids included in group plan queries. */
  groupIds: readonly string[];
  /** Optional map bounds used to query plan documents. Omit for unbounded initial loads. */
  region?: MapRegion;
  /** Value that forces a fresh query for the same ids and optional map bounds. */
  refreshKey?: AppQueryKeyValue;
  /** User ids included in user-owned plan queries. */
  userIds: readonly string[];
};

export type UseExplorerUsersQueryOptions = {
  /** Value that forces a fresh user query for the same user ids. */
  refreshKey?: AppQueryKeyValue;
  /** User ids loaded for residence markers and participant names. */
  userIds: readonly string[];
};

export type UseExplorerGroupsQueryOptions = {
  /** Group ids loaded for marker display and scoped search. */
  groupIds: readonly string[];
};

export function useExplorerPlansQuery({
  enabled = true,
  groupIds,
  region,
  refreshKey,
  userIds,
}: UseExplorerPlansQueryOptions) {
  const normalizedGroupIds = createQueryIdListKeyPart(groupIds);
  const normalizedUserIds = createQueryIdListKeyPart(userIds);
  const resolvedPlansPerQueryLimit = DEFAULT_EXPLORER_REGION_QUERY_LIMIT;
  const isEnabled = enabled && (normalizedGroupIds.length > 0 || normalizedUserIds.length > 0);
  const query = useAppQuery({
    queryKey: createQueryKey(
      AppQueryDomain.EXPLORER,
      AppQueryKeySegment.REGION_PLANS,
      normalizedUserIds,
      normalizedGroupIds,
      region ? getMapRegionKeyPart(region) : null,
      resolvedPlansPerQueryLimit,
      nullableQueryKeyPart(refreshKey)
    ),
    queryFn: () =>
      getExplorerPlans({
        groupIds: normalizedGroupIds,
        region,
        userIds: normalizedUserIds,
        plansPerQueryLimit: resolvedPlansPerQueryLimit,
      }),
    enabled: isEnabled,
    retry: false,
    staleTime: 0,
  });

  return {
    dataUpdatedAt: query.dataUpdatedAt,
    loading: query.isFetching,
    plans: isEnabled ? (query.data ?? []) : [],
  };
}

export function useExplorerUsersQuery({ refreshKey, userIds }: UseExplorerUsersQueryOptions) {
  const normalizedUserIds = createQueryIdListKeyPart(userIds);
  const isEnabled = normalizedUserIds.length > 0;
  const query = useAppQuery({
    queryKey: createQueryKey(
      AppQueryDomain.EXPLORER,
      AppQueryKeySegment.USERS,
      normalizedUserIds,
      nullableQueryKeyPart(refreshKey)
    ),
    queryFn: () => getExplorerUsers(normalizedUserIds),
    enabled: isEnabled,
    placeholderData: [],
    retry: false,
  });

  return {
    loading: query.isFetching,
    users: isEnabled ? (query.data ?? []) : [],
  };
}

export function useExplorerGroupsQuery({ groupIds }: UseExplorerGroupsQueryOptions) {
  const normalizedGroupIds = createQueryIdListKeyPart(groupIds);
  const isEnabled = normalizedGroupIds.length > 0;
  const query = useAppQuery({
    queryKey: createQueryKey(
      AppQueryDomain.EXPLORER,
      AppQueryKeySegment.GROUPS,
      normalizedGroupIds
    ),
    queryFn: () => getGroupsIn([...normalizedGroupIds]),
    enabled: isEnabled,
    placeholderData: [],
    retry: false,
  });

  return {
    groups: isEnabled ? (query.data ?? []) : [],
  };
}

function getMapRegionKeyPart(region: MapRegion) {
  return {
    latitude: region.latitude,
    latitudeDelta: region.latitudeDelta,
    longitude: region.longitude,
    longitudeDelta: region.longitudeDelta,
  };
}

function getPlanQueryConstraints(limit: number): db.QueryConstraint[] {
  return [db.orderBy('createdAt', 'desc'), db.limit(limit)];
}

async function getExplorerPlans({
  groupIds,
  plansPerQueryLimit,
  region,
  userIds,
}: {
  groupIds: readonly string[];
  plansPerQueryLimit: number;
  region?: MapRegion;
  userIds: readonly string[];
}) {
  const constraints = getPlanQueryConstraints(plansPerQueryLimit);
  const [loadedUserPlans, loadedGroupPlans] = await Promise.all([
    userIds.length === 0
      ? []
      : region
        ? getUsersPlansInMapBounds([...userIds], region, constraints)
        : getUsersPlans([...userIds], constraints),
    groupIds.length === 0
      ? []
      : region
        ? getGroupsPlansInMapBounds([...groupIds], region, constraints)
        : getGroupsPlans([...groupIds], constraints),
  ]);

  // Joined-plan helpers and indexes are ready, but Explorer must not call them yet:
  // Firestore rules can authorize following the plan owner, but not following any joined user.
  // Enable this after adding backend-owned userAccess/{userId}.followingIds and matching rules.
  // const loadedJoinedPlans = await (
  //   region
  //     ? getUsersJoinedPlansInMapBounds([...userIds], region, constraints)
  //     : getUsersJoinedPlans([...userIds], constraints)
  // );

  return uniqueBy([...loadedUserPlans, ...loadedGroupPlans], 'planId');
}

async function getExplorerUsers(userIds: readonly string[]): Promise<User[]> {
  const users = await getUsersIn([...userIds]);
  return uniqueBy(users, 'userId');
}
